import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import FormalRVHeader from '../components/formalrv/FormalRVHeader'
import Explorer, { loadExample } from '../components/formalrv/Explorer'
import Workspace from '../components/formalrv/Workspace'
import Prose from '../components/formalrv/Prose'
import { ArtifactViewer, Questions } from '../components/formalrv/Review'
import exerciseData from '../components/formalrv/exercises.json'
import { grade, type CheckResult, type Exercise, type Outputs } from '../components/formalrv/grader'
import type { FRXExample } from '../utils/api'

export const EXERCISES = exerciseData as unknown as {
  units: { id: string; title: string; chapter: string; summary: string }[]
  exercises: Exercise[]
}

// ---------------------------------------------------------------------------------------------
// Progress and drafts: a convenience kept in this browser only.
// ---------------------------------------------------------------------------------------------

const PROGRESS_KEY = 'frv.exercises.v1'
interface Progress { solved: Record<string, boolean>; drafts: Record<string, string> }

export function readProgress(): Progress {
  try {
    const p = JSON.parse(localStorage.getItem(PROGRESS_KEY) ?? '{}')
    return { solved: p.solved ?? {}, drafts: p.drafts ?? {} }
  } catch { return { solved: {}, drafts: {} } }
}

function saveProgress(update: (p: Progress) => void) {
  try { const p = readProgress(); update(p); localStorage.setItem(PROGRESS_KEY, JSON.stringify(p)) } catch { /* private mode */ }
}

// ---------------------------------------------------------------------------------------------

function CheckList({ ex, results }: { ex: Exercise; results: CheckResult[] | null }) {
  return (
    <ul className="space-y-1.5">
      {ex.checks.map((c, i) => {
        const r = results?.[i]
        const state = !r ? 'idle' : r.pass ? 'pass' : 'fail'
        return (
          <li key={c.label} className="flex gap-2 text-sm">
            <span aria-hidden className={`mt-0.5 w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold ${
              state === 'pass' ? 'bg-emerald-600 text-white' : state === 'fail' ? 'bg-rose-600 text-white' : 'border-2 border-slate-300 text-slate-400'}`}>
              {state === 'pass' ? '✓' : state === 'fail' ? '✗' : ''}
            </span>
            <div className="min-w-0">
              <div className="text-qcloud-text"><Prose text={c.label} /></div>
              {r && <div className={`text-xs ${r.pass ? 'text-qcloud-muted' : 'text-rose-700'} break-words`}>{r.detail}</div>}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

export default function FormalRVExercisePage() {
  const { exerciseId } = useParams<{ exerciseId: string }>()
  const index = EXERCISES.exercises.findIndex(e => e.id === exerciseId)
  const ex = EXERCISES.exercises[index]
  const unit = EXERCISES.units.find(u => u.id === ex?.unit)
  const [base, setBase] = useState<FRXExample | null>(null)
  const [results, setResults] = useState<CheckResult[] | null>(null)
  const [hints, setHints] = useState(0)
  const [showSolution, setShowSolution] = useState(false)
  const [solved, setSolved] = useState(false)
  const [restart, setRestart] = useState(0)

  useEffect(() => {
    if (!ex) return
    document.title = `${ex.title} · QParam exercises`
    setResults(null); setHints(0); setShowSolution(false); setSolved(!!readProgress().solved[ex.id])
    if (ex.kind === 'review') { setBase(null); return }
    loadExample(ex.kind === 'sysprog' && ex.context ? ex.context : 'pauli').then(setBase).catch(() => setBase(null))
  }, [ex])

  const example = useMemo<FRXExample | null>(() => {
    if (!ex || !base) return null
    const draft = restart ? undefined : readProgress().drafts[ex.id]
    const common = { ...base, steps: [], passes: [], vm: null, variants: [], lean: [], machine: '', machine_path: '', includes: [], qec_opts: [] }
    return ex.kind === 'sysprog'
      ? { ...common, id: `exercise-${ex.id}`, title: ex.title, texts: { prog: base.texts.prog, sysprog: draft ?? ex.starter } }
      : { ...common, id: `exercise-${ex.id}`, unit: 'Main', title: ex.title, source_path: 'Main.prog', specialize: ex.point ?? {},
          texts: { prog: draft ?? ex.starter, sysprog: '' } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ex, base, restart])

  const record = (outputs: Outputs, text: string) => {
    if (!ex) return
    const r = grade(ex, outputs)
    setResults(r)
    const ok = r.every(x => x.pass)
    saveProgress(p => { p.drafts[ex.id] = text; if (ok) p.solved[ex.id] = true })
    if (ok) setSolved(true)
  }

  if (!ex) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-qcloud-light via-white to-qcloud-bg">
        <FormalRVHeader crumb="Exercises" />
        <main className="max-w-3xl mx-auto px-6 py-12 text-qcloud-muted">No such exercise. <Link to="/shor/learn#exercises" className="text-qcloud-primary">All exercises</Link></main>
      </div>
    )
  }

  const prev = EXERCISES.exercises[index - 1]
  const next = EXERCISES.exercises[index + 1]
  const allPass = !!results && results.every(r => r.pass)
  const small = 'h-8 px-3 rounded-md border border-qcloud-border text-sm hover:border-qcloud-primary'

  const side = (
    <div className="space-y-5">
      <div>
        <Link to="/shor/learn#exercises" className="text-xs text-qcloud-muted hover:text-qcloud-primary">← Exercises</Link>
        <div className="text-xs font-semibold text-qcloud-muted mt-3">{unit?.title} · {ex.chapter}</div>
        <h1 className="text-2xl font-bold text-qcloud-primary leading-tight">
          {ex.title}{solved && <span className="ml-2 align-middle text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">solved</span>}
        </h1>
      </div>
      <div className="space-y-2.5 text-[14.5px] leading-relaxed text-qcloud-text">
        {ex.goal.map((g, i) => <p key={i}><Prose text={g} /></p>)}
      </div>

      {ex.kind === 'review' ? (
        <div className="space-y-2">
          <h2 className="text-xs font-bold uppercase tracking-wider text-qcloud-muted">Questions</h2>
          <Questions key={ex.id} questions={ex.questions ?? []}
            onAllCorrect={() => { saveProgress(p => { p.solved[ex.id] = true }); setSolved(true) }} />
        </div>
      ) : (
        <div className="space-y-2">
          <h2 className="text-xs font-bold uppercase tracking-wider text-qcloud-muted">
            Checks{allPass && <span className="ml-2 normal-case tracking-normal text-emerald-700">all pass</span>}
          </h2>
          <CheckList ex={ex} results={results} />
          {!results && <p className="text-xs text-qcloud-muted">{ex.kind === 'sysprog' ? 'Edit the .sysprog, then ✓ Check.' : 'Edit the source, then ▶ Compile.'}</p>}
        </div>
      )}

      <div className="space-y-2">
        {ex.hints.slice(0, hints).map((h, i) => (
          <p key={i} className="text-sm text-qcloud-text bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"><Prose text={h} /></p>
        ))}
        <div className="flex flex-wrap gap-1.5">
          {hints < ex.hints.length && <button onClick={() => setHints(hints + 1)} className={small}>Hint</button>}
          {ex.kind !== 'review' && <>
            <button onClick={() => setShowSolution(!showSolution)} className={small}>{showSolution ? 'Hide solution' : 'Solution'}</button>
            <button onClick={() => { saveProgress(p => { delete p.drafts[ex.id] }); setResults(null); setRestart(r => r + 1) }}
              className="h-8 px-3 text-sm text-qcloud-muted hover:text-qcloud-text">Start over</button>
          </>}
        </div>
        {showSolution && ex.kind !== 'review' && (
          <pre className="text-[12px] leading-relaxed bg-slate-900 text-slate-100 rounded-lg p-3 overflow-x-auto max-h-72">{
            ex.kind === 'sysprog' ? solutionDiff(ex.starter, ex.solution) : ex.solution}</pre>
        )}
      </div>

      <nav className="flex justify-between gap-3 pt-3 border-t border-slate-100 text-sm">
        {prev ? <Link to={`/shor/learn/exercises/${prev.id}`} className="text-qcloud-primary">← {prev.title}</Link> : <span />}
        {next ? <Link to={`/shor/learn/exercises/${next.id}`} className="font-semibold text-qcloud-primary text-right">{next.title} →</Link>
          : <Link to="/shor/learn#exercises" className="font-semibold text-qcloud-primary text-right">All exercises →</Link>}
      </nav>
    </div>
  )

  return (
    <Workspace crumb="Exercises" side={side} sideLabel="Task">
      {ex.kind === 'review'
        ? <ArtifactViewer key={ex.id} artifacts={ex.artifacts ?? []} />
        : example && (
          <Explorer
            key={`${ex.id}-${restart}`}
            exampleId={example.id}
            example={example}
            playground
            hideCompile={ex.kind === 'sysprog'}
            initialPane={ex.kind === 'sysprog' ? 'sysprog' : 'qppm'}
            onSourceChange={text => saveProgress(p => { p.drafts[ex.id] = text })}
            onCompiled={({ steps, texts }) => record({
              prog: texts.prog,
              accepted: !!steps[0] && steps[0].exit === 0 && !!texts.qppm,
              acceptLog: steps[0]?.log ?? '',
              lowered: steps[1] ? steps[1].exit === 0 && !!texts.qec : null,
              lowerLog: steps[1]?.log ?? '',
              qppm: texts.qppm, qec: texts.qec,
            }, texts.prog)}
            onVmChecked={({ sysprog, verdict }) => record({
              accepted: true, acceptLog: '', lowered: null, lowerLog: '', sysprog, vm: verdict, starterSysprog: ex.starter,
            }, sysprog)}
          />
        )}
    </Workspace>
  )
}

/** For a listing exercise, only the lines that change. */
function solutionDiff(before: string, after: string): string {
  const a = before.split('\n'), b = after.split('\n')
  const out: string[] = []
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    if (a[i] !== b[i]) { if (a[i] !== undefined) out.push(`- ${a[i]}`); if (b[i] !== undefined) out.push(`+ ${b[i]}`) }
  }
  return out.join('\n')
}
